import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Bell } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { fmtDate } from "@/lib/api";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { Badge } from "@/admin/hr/common";

const CARDS = [
  ["total", "Toplam başvuru", "/admin/basvurular"], ["today", "Bugünkü başvurular", "/admin/basvurular"],
  ["last7", "Son 7 gün", "/admin/basvurular"], ["new", "Yeni başvurular", "/admin/basvurular"],
  ["to_call", "Geri aranacak kişiler", "/admin/geri-arama"], ["missing_docs", "Evrakı eksik adaylar", "/admin/belgeler"],
  ["silahli", "Silahlı aday", "/admin/basvurular"], ["silahsiz", "Silahsız aday", "/admin/basvurular"],
  ["suitable", "Uygun bulunan", "/admin/basvurular"], ["hired", "İşe alınan", "/admin/basvurular"],
];

function EmailAlert({ d }) {
  if (!d.notification_emails?.length) {
    return <div className="mb-6 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="email-warning-missing"><AlertTriangle className="h-5 w-5 shrink-0" /><span>Bildirim e-posta adresi tanımlı değil. <Link to="/admin/bildirimler" className="font-bold underline">Bildirim Ayarları</Link></span></div>;
  }
  if ((d.email_status && !d.email_status.ok) || d.email_failed > 0) {
    return <div className="mb-6 flex gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900" data-testid="email-warning-failed"><AlertTriangle className="h-5 w-5 shrink-0" /><span>{d.email_failed} bildirim e-postası gönderilemedi{d.email_status?.error ? ` (son hata: ${d.email_status.error})` : ""}. <Link to="/admin/bildirimler" className="font-bold underline">Kayıtları gör ve yeniden dene</Link></span></div>;
  }
  return <div className="mb-6 flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="email-status-ok"><CheckCircle2 className="h-5 w-5 shrink-0" /><span>Bildirimler şu adres(ler)e gidiyor: <b>{d.notification_emails.join(", ")}</b>{d.email_status ? ` · Son gönderim: ${fmtDate(d.email_status.at)}` : ""}</span></div>;
}

function Site() {
  const { data } = useFetch("/admin/dashboard");
  if (!data) return null;
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      {[["quotes", "Teklif talepleri", "/admin/teklifler"], ["messages", "İletişim mesajları", "/admin/mesajlar"]].map(([k, l, to]) => (
        <Link key={k} to={to} className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-4 text-sm hover:border-[color:var(--red)]" data-testid={`dash-site-${k}`}>
          <span>{l}</span><span><b className="text-lg">{data[k].total}</b> <span className="text-xs text-[color:var(--red)]">{data[k].new} yeni</span></span>
        </Link>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { data: d } = useFetch("/admin/hr/stats");
  if (!d) return <Spinner />;
  return (
    <div>
      <AdminTitle title="Genel Bakış" desc="Tüm sayılar gerçek veritabanı kayıtlarından hesaplanır." />
      <EmailAlert d={d} />
      {d.expiring > 0 && <Link to="/admin/basvurular" className="mb-6 flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900" data-testid="dash-expiring"><AlertTriangle className="h-5 w-5 shrink-0" /> {d.expiring} adayın kimlik kartı süresi 60 gün içinde doluyor veya dolmuş.</Link>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {CARDS.map(([k, l, to]) => (
          <Link key={k} to={to} className="rounded-lg border border-zinc-200 bg-white p-4 transition-colors hover:border-[color:var(--red)]" data-testid={`stat-${k}`}>
            <p className="text-xs text-zinc-500">{l}</p>
            <p className="mt-1 text-3xl font-extrabold text-zinc-950" data-testid={`stat-${k}-value`}>{d[k]}</p>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-zinc-200 bg-white" data-testid="dash-reminders">
          <h2 className="border-b border-zinc-200 px-5 py-3 text-sm font-bold">Bekleyen geri arama hatırlatmaları</h2>
          {d.reminders.length === 0 ? <p className="p-5 text-sm text-zinc-500">Bekleyen hatırlatma yok.</p> : (
            <ul className="divide-y divide-zinc-100">{d.reminders.map((r) => (
              <li key={r.id}><Link to={`/admin/${r.ref_coll === "callbacks" ? "geri-arama" : "basvurular"}?id=${r.ref_id}`} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm hover:bg-zinc-50">
                <span><Bell className={`mr-1 inline h-4 w-4 ${new Date(r.due_at) < new Date() ? "text-red-600" : "text-amber-600"}`} /><b>{r.name}</b> · {r.phone} {r.note && <span className="text-zinc-500">· {r.note}</span>}</span>
                <span className="text-xs text-zinc-500">{new Date(r.due_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</span>
              </Link></li>
            ))}</ul>
          )}
        </section>
        <section className="rounded-lg border border-zinc-200 bg-white" data-testid="dash-recent">
          <h2 className="border-b border-zinc-200 px-5 py-3 text-sm font-bold">Son başvurular</h2>
          {d.recent.length === 0 ? <p className="p-5 text-sm text-zinc-500">Henüz başvuru yok.</p> : (
            <ul className="divide-y divide-zinc-100">{d.recent.map((a) => (
              <li key={a.id}><Link to={`/admin/basvurular?id=${a.id}`} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm hover:bg-zinc-50">
                <span><b className="text-[color:var(--red)]">{a.app_no}</b> {a.name} <span className="text-zinc-500">· {a.position}</span></span>
                <span className="flex items-center gap-2 text-xs text-zinc-500"><Badge value={a.status} /> {fmtDate(a.created_at)}</span>
              </Link></li>
            ))}</ul>
          )}
        </section>
      </div>
      <Site />
    </div>
  );
}
